import type { CSSProperties } from "react";

export type HeroMusicVisualControls = {
  radius: number;
  dragResponsiveness: number;
  maxContainerWidth: number;
  height: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  clipOverflow: boolean;
  cameraFov: number;
  cameraY: number;
  cameraZ: number;
  size: number;
  carouselTiltX: number;
  carouselTiltZ: number;
  coverWidth: number;
  coverHeight: number;
  coverOffsetY: number;
  coverRadiusOffset: number;
  coverAngleOffset: number;
  coverRotationZ: number;
  coverBorderRadius: number;
  coverOpacity: number;
  coverBackfaceColor: string;
  redOverlayEnabled: boolean;
  springTension: number;
  springFriction: number;
  dragTension: number;
  dragFriction: number;
};

type HeroMusicVisualLayoutStyle = CSSProperties & Record<`--hero-music-${string}`, string>;

export const DEFAULT_HERO_MUSIC_VISUAL_CONTROLS: HeroMusicVisualControls = {
  radius: 2,
  dragResponsiveness: 1,
  maxContainerWidth: 1200,
  height: {
    mobile: 350,
    tablet: 400,
    desktop: 500,
  },
  clipOverflow: true,
  cameraFov: 54,
  cameraY: -0.03,
  cameraZ: 4.92,
  size: 2.5,
  carouselTiltX: 0,
  carouselTiltZ: 0,
  coverWidth: 1,
  coverHeight: 1,
  coverOffsetY: 0,
  coverRadiusOffset: 0,
  coverAngleOffset: 0,
  coverRotationZ: 0,
  coverBorderRadius: 0.075,
  coverOpacity: 1,
  coverBackfaceColor: "#F2F0ED",
  redOverlayEnabled: false,
  springTension: 180,
  springFriction: 26,
  dragTension: 300,
  dragFriction: 30,
};

export const HERO_MUSIC_VISUAL_FRAME_CLASS_NAME =
  "tw-h-[var(--hero-music-visual-height)] tw-var-[hero-music-visual-height=var(--hero-music-visual-height-mobile)] sm:tw-var-[hero-music-visual-height=var(--hero-music-visual-height-tablet)] lg:tw-var-[hero-music-visual-height=var(--hero-music-visual-height-desktop)]";

export const HERO_MUSIC_VISUAL_OVERLAY_CLASS_NAME =
  "tw-var-[hero-music-overlay-perspective=var(--hero-music-overlay-perspective-mobile)] sm:tw-var-[hero-music-overlay-perspective=var(--hero-music-overlay-perspective-tablet)] lg:tw-var-[hero-music-overlay-perspective=var(--hero-music-overlay-perspective-desktop)] tw-var-[hero-music-cover-width=var(--hero-music-cover-width-mobile)] sm:tw-var-[hero-music-cover-width=var(--hero-music-cover-width-tablet)] lg:tw-var-[hero-music-cover-width=var(--hero-music-cover-width-desktop)] tw-var-[hero-music-cover-height=var(--hero-music-cover-height-mobile)] sm:tw-var-[hero-music-cover-height=var(--hero-music-cover-height-tablet)] lg:tw-var-[hero-music-cover-height=var(--hero-music-cover-height-desktop)]";

export const HERO_MUSIC_VISUAL_LAYOUT_STYLE: HeroMusicVisualLayoutStyle = createHeroMusicVisualLayoutStyle(
  DEFAULT_HERO_MUSIC_VISUAL_CONTROLS,
);

function createHeroMusicVisualLayoutStyle(controls: HeroMusicVisualControls) {
  const mobileLayout = getHeroMusicVisualLayout(controls, controls.height.mobile);
  const tabletLayout = getHeroMusicVisualLayout(controls, controls.height.tablet);
  const desktopLayout = getHeroMusicVisualLayout(controls, controls.height.desktop);

  return {
    "--hero-music-visual-height-mobile": `${controls.height.mobile}px`,
    "--hero-music-visual-height-tablet": `${controls.height.tablet}px`,
    "--hero-music-visual-height-desktop": `${controls.height.desktop}px`,
    "--hero-music-overlay-perspective-mobile": `${mobileLayout.overlayPerspective}px`,
    "--hero-music-overlay-perspective-tablet": `${tabletLayout.overlayPerspective}px`,
    "--hero-music-overlay-perspective-desktop": `${desktopLayout.overlayPerspective}px`,
    "--hero-music-cover-width-mobile": `${mobileLayout.coverWidth}px`,
    "--hero-music-cover-width-tablet": `${tabletLayout.coverWidth}px`,
    "--hero-music-cover-width-desktop": `${desktopLayout.coverWidth}px`,
    "--hero-music-cover-height-mobile": `${mobileLayout.coverHeight}px`,
    "--hero-music-cover-height-tablet": `${tabletLayout.coverHeight}px`,
    "--hero-music-cover-height-desktop": `${desktopLayout.coverHeight}px`,
  } satisfies HeroMusicVisualLayoutStyle;
}

function getHeroMusicVisualLayout(controls: HeroMusicVisualControls, height: number) {
  const overlayPerspective = getHeroMusicOverlayPerspective(controls, height);

  return {
    overlayPerspective,
    coverWidth: controlsToPx(controls.coverWidth, overlayPerspective, controls.cameraZ),
    coverHeight: controlsToPx(controls.coverHeight, overlayPerspective, controls.cameraZ),
  };
}

function controlsToPx(value: number, overlayPerspective: number, cameraZ: number) {
  return value * getOverlayScale(overlayPerspective, cameraZ);
}

function getOverlayScale(overlayPerspective: number, cameraZ: number) {
  return overlayPerspective / cameraZ;
}

export function getHeroMusicOverlayScale(overlayPerspective: number, cameraZ: number) {
  return getOverlayScale(overlayPerspective, cameraZ);
}

export function getHeroMusicOverlayPerspective(controls: HeroMusicVisualControls, height: number) {
  const fov = (controls.cameraFov * Math.PI) / 180;
  const effectiveFov = 2 * Math.atan(Math.tan(fov / 2) / controls.size);

  return height / 2 / Math.tan(effectiveFov / 2);
}
